import { Injectable, inject } from '@angular/core';
import { type Firestore } from 'firebase/firestore';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import { BULK_ACTION_REQUEST_TIMEOUT_MS } from '../utils/bulk-selection';
import { DISPO_LIST_SALE } from '../data/call/result';
import { log } from '../utils/logger';
import {
  BackgroundJob,
  BackgroundJobStatus,
  Policy,
  PolicyHistoryEntry,
  PolicyCrosswalkCandidate,
  PolicyCrosswalk,
  DeclineReason,
  declineReasonToJSON,
  Enrollment,
  Type,
  typeToJSON,
  PolicyStatus,
  PolicyStatusCategory,
  policyStatusCategoryToJSON,
  policyStatusToJSON,
  TerminationReason,
  terminationReasonToJSON,
  DialerLeadCall,
  CustomField,
  consumer_Dialer_ResultToJSON,
} from '@enrollhere/classes';
import {
  AggregationRequest,
  AggregationResponse,
} from '../interfaces/policy-stats.interface';
import { httpsCallable, type Functions } from 'firebase/functions';
import { FIRESTORE, FUNCTIONS } from '../firebase/firebase.tokens';
import type {
  PolicyTeamTagsPreview,
  PolicyTeamTagsPreviewRequest,
  PolicyUpdateRequest,
} from './policy-participants.types';
import { AgencyCustomFieldService } from './agency-custom-field.service';
import { policyOwnerSaveErrorMessage } from './policy-owner-errors';

const logger = log.getLogger('policy.service');

/** Route/document id wins when API body id was wiped (e.g. bad partial merge). */
export function normalizePolicyDocumentId(
  policy: Policy,
  documentId: string
): Policy {
  const routeId = documentId.trim();
  if (!routeId || policy.id?.trim()) {
    return policy;
  }
  return Policy.fromPartial({ ...policy, id: routeId });
}

export type PolicyDuplicateReviewFinding = {
  crosswalkCandidates?: PolicyCrosswalkCandidate[];
  fingerprint: string;
  agencyId: string;
  key: string;
  keyType: string;
  policyIds: string[];
  policies: Policy[];
  policyCount: number;
  suggestedPrimaryId?: string;
  confidence?: string;
  evidenceKinds?: string[];
  categoryId?: string;
  categoryLabel?: string;
  bulkMergeEligible?: boolean;
  detectedAt?: string | Date;
  runTimestamp?: string | Date;
  resolutionState?: number;
  resolvedByUserId?: string;
  resolvedAt?: string | Date;
};

export interface AgencyPolicyCustomFields {
  agencyId: string;
  fields: CustomField[];
}

export interface PolicyCustomFieldsBatchResult {
  definitions: AgencyPolicyCustomFields[];
  failedAgencyIds: string[];
}

interface PolicyBulkResultBase {
  policyId: string;
  agencyId?: string;
}

export type PolicyBulkResult =
  | (PolicyBulkResultBase & { outcome: 'would-update' })
  | (PolicyBulkResultBase & {
      outcome: 'updated';
      historyEntryId?: string;
      indexSyncPending?: boolean;
      warning?: string;
    })
  | (PolicyBulkResultBase & {
      outcome: 'skipped';
      skipReason?: string;
    })
  | (PolicyBulkResultBase & {
      outcome: 'failed';
      error?: string;
    });

interface PolicyBulkResponseBase {
  success: boolean;
  dryRun: boolean;
  totalMatched: number;
  totalEligible: number;
  totalWouldUpdate: number;
  totalUpdated: number;
  totalSkipped: number;
  totalFailed: number;
  results: PolicyBulkResult[];
}

interface PolicyBulkClientError {
  success: false;
  error: string;
}

export type PolicyStatusBulkResult =
  (PolicyBulkResponseBase & { status: string }) | PolicyBulkClientError;

export type PolicyOwnerBulkResult =
  (PolicyBulkResponseBase & { ownerId: string }) | PolicyBulkClientError;

export interface PolicyLeadListBulkRow {
  id: string;
  agencyId?: string;
  displayName?: string;
  leadId?: string;
  outcome: 'would-update' | 'updated' | 'skipped' | 'failed';
  skipReason?: string;
  error?: string;
}

export type PolicyLeadListBulkResult =
  | {
      totalMatched: number;
      totalUpdated: number;
      totalSkipped: number;
      totalFailed: number;
      results: PolicyLeadListBulkRow[];
      warning?: string;
    }
  | PolicyBulkClientError;

export interface PolicyWorkflowBulkRow {
  id: string;
  agencyId?: string;
  displayName?: string;
  outcome: 'would-update' | 'updated' | 'skipped' | 'failed';
  runId?: string;
  skipReason?: string;
  error?: string;
}

export type PolicyWorkflowBulkResult =
  | {
      totalMatched: number;
      totalUpdated: number;
      totalSkipped: number;
      totalFailed: number;
      results: PolicyWorkflowBulkRow[];
    }
  | PolicyBulkClientError;

export class PolicyCustomFieldsLoadError extends Error {
  override readonly name = 'PolicyCustomFieldsLoadError';

  constructor(readonly failedAgencyIds: string[]) {
    super(
      `Unable to load policy custom fields for: ${failedAgencyIds.join(', ')}`
    );
  }
}

/**
 * Policy Service
 * HTTP client for policy management API
 * Reusable across all products
 */
@Injectable({
  providedIn: 'root',
})
export class PolicyService {
  private readonly enrollhereApi = inject(EnrollhereApiService);
  private readonly customFieldService = inject(AgencyCustomFieldService);
  private readonly firestore: Firestore = inject(FIRESTORE);
  /** @deprecated Direct Firebase Functions access is deprecated. Use API endpoints instead. */
  private readonly functions: Functions = inject(FUNCTIONS);

  async searchPolicies(params: {
    filter?: any;
    orderBy?: {
      field: string;
      direction: 'asc' | 'desc';
      nestedPath?: string;
      nestedFilter?: { field: string; value: string };
    };
    size?: number;
    nextPageToken?: string;
  }): Promise<{
    policies: Policy[];
    total: number;
    nextPageToken?: string;
    size: number;
  }> {
    try {
      const { ...cleanParams } = params;
      const response = await this.enrollhereApi.post<{
        policies: unknown[];
        total: number;
        nextPageToken?: string;
        size: number;
      }>('policy/search', cleanParams);

      return {
        policies: (response.policies || []).map((p: any) => Policy.fromJSON(p)),
        total: response.total || 0,
        nextPageToken: response.nextPageToken,
        size: response.size || 0,
      };
    } catch (error) {
      logger.error('Error searching policies', error);
      return { policies: [], total: 0, size: 0 };
    }
  }

  /** Load policy custom-field definitions from the agency catalog API. */
  async getPolicyCustomFields(
    agencyId: string
  ): Promise<AgencyPolicyCustomFields> {
    const normalizedAgencyId = agencyId.trim();
    if (!normalizedAgencyId) {
      return { agencyId: normalizedAgencyId, fields: [] };
    }

    const catalog =
      await this.customFieldService.getCatalog(normalizedAgencyId);
    return {
      agencyId: normalizedAgencyId,
      fields: catalog.policyFields,
    };
  }

  async getPolicyCustomFieldsForAgencies(
    agencyIds: string[],
    concurrency = 4
  ): Promise<PolicyCustomFieldsBatchResult> {
    const uniqueAgencyIds = [
      ...new Set(agencyIds.map(id => id.trim()).filter(Boolean)),
    ];
    if (uniqueAgencyIds.length === 0) {
      return { definitions: [], failedAgencyIds: [] };
    }

    const definitions: AgencyPolicyCustomFields[] = [];
    const failedAgencyIds: string[] = [];
    const batchSize = Math.max(1, Math.floor(concurrency));
    for (let offset = 0; offset < uniqueAgencyIds.length; offset += batchSize) {
      const batch = uniqueAgencyIds.slice(offset, offset + batchSize);
      const settled = await Promise.allSettled(
        batch.map(agencyId => this.getPolicyCustomFields(agencyId))
      );
      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          definitions.push(result.value);
          return;
        }
        const agencyId = batch[index];
        failedAgencyIds.push(agencyId);
        logger.error(
          `Error fetching policy custom fields for ${agencyId}`,
          result.reason
        );
      });
    }

    if (definitions.length === 0 && failedAgencyIds.length > 0) {
      throw new PolicyCustomFieldsLoadError(failedAgencyIds);
    }

    return {
      definitions,
      failedAgencyIds,
    };
  }

  /**
   * Search for policies submitted today by a specific agent for a specific lead phone.
   * Used by the dispo product-entry modal to surface policies the agent submitted earlier
   * today (potentially linked to a different call) so they can be attached to the
   * current call's productSales summary.
   */
  async searchTodayPoliciesForLeadByAgent(params: {
    agentId: string;
    leadPhone: string;
  }): Promise<Policy[]> {
    const { agentId, leadPhone } = params;
    if (!agentId || !leadPhone) return [];

    const result = await this.searchPolicies({
      filter: {
        'agent-id': [{ id: agentId }],
        'consumer-phone': leadPhone,
        'submitted-date': { range: 'today' },
      },
      orderBy: {
        field: 'submittedAt.timestamp',
        direction: 'desc',
      },
      size: 50,
    });
    return result.policies;
  }

  /**
   * Get policy by ID
   */
  async getPolicyById(
    policyId: string,
    include?: string[]
  ): Promise<Policy | null> {
    const routeId = policyId?.trim();
    if (!routeId) {
      return null;
    }
    try {
      const params = include ? { include: include.join(',') } : {};
      const queryString = params.include ? `?include=${params.include}` : '';
      const response = await this.enrollhereApi.get<{ policy: unknown }>(
        `policy/${routeId}${queryString}`
      );
      if (!response.policy) {
        return null;
      }
      return normalizePolicyDocumentId(
        Policy.fromJSON(response.policy),
        routeId
      );
    } catch (error) {
      logger.error('Error fetching policy', error);
      return null;
    }
  }

  /**
   * Get related policies for a given policy ID.
   * Calls the detail endpoint which returns related matches.
   */
  async getRelatedPolicies(policyId: string): Promise<
    Array<{
      policy: Policy;
      matchedBy: Array<'phone' | 'medicare' | 'email' | 'nameBirth'>;
    }>
  > {
    try {
      const response = await this.enrollhereApi.get<{
        relatedPolicies?: Array<{
          policy: unknown;
          matchedBy: Array<'phone' | 'medicare' | 'email' | 'nameBirth'>;
        }>;
      }>(`policy/${policyId}`);
      if (!Array.isArray(response.relatedPolicies)) {
        return [];
      }
      return response.relatedPolicies
        .map(row => {
          try {
            const parsed = Policy.fromJSON(row.policy);
            const docId =
              typeof parsed.id === 'string' && parsed.id.trim()
                ? parsed.id.trim()
                : undefined;
            return {
              policy: docId ? normalizePolicyDocumentId(parsed, docId) : parsed,
              matchedBy: row.matchedBy ?? [],
            };
          } catch {
            return null;
          }
        })
        .filter(
          (
            r
          ): r is {
            policy: Policy;
            matchedBy: Array<'phone' | 'medicare' | 'email' | 'nameBirth'>;
          } => r !== null
        );
    } catch (error) {
      logger.error('Error fetching related policies', error);
      return [];
    }
  }

  /**
   * Server-side bulk merge of duplicate policy pairs, or all eligible pairs
   * in a STRONG category (chunked via nextPageToken).
   */
  /**
   * Start a bulk merge as a background job and return its id.
   *
   * Unlike `bulkMergePolicies`, which merges synchronously while the browser
   * pages a cursor, this hands the whole drain to a worker: it survives the
   * tab closing and can be cancelled server-side.
   */
  async startBulkMergeJob(params: {
    agencyId: string;
    categoryId?: string;
    batchSize?: number;
  }): Promise<{ jobId: string }> {
    return this.enrollhereApi.post<{ jobId: string }>(
      'policy/merge/bulk/job',
      params
    );
  }

  /**
   * Runs a bulk policy action ("select all matching filter" in the Policy
   * Book) as a background job, so the selection is not bounded by the
   * synchronous policy bulk routes' `maxPolicies` cap. Mirrors the
   * dialer's `startBulkLeadActionJob` contract exactly. Filter-only by
   * design — an explicit id selection is already bounded by how many rows
   * a person can click, so it keeps using the synchronous routes above.
   */
  async startBulkPolicyActionJob(params: {
    kind:
      | 'status'
      | 'owner'
      | 'add-to-lead-list'
      | 'remove-from-lead-list'
      | 'start-workflow';
    agencyId: string;
    filter: Record<string, unknown>;
    status?: string;
    statusReason?: string;
    statusSource?: 'policy-book' | 'data-health';
    dryRun?: boolean;
    ownerId?: string;
    /** Kind owner only. False leaves the policies' team tags unchanged. */
    updateTeamTags?: boolean;
    leadListId?: string;
    note?: string;
    workflowId?: string;
    requestId?: string;
  }): Promise<{ jobId: string }> {
    return this.enrollhereApi.post<{ jobId: string }>(
      'policy/bulk/action-job',
      {
        kind: params.kind,
        agencyId: params.agencyId,
        filter: params.filter,
        ...(params.status === undefined ? {} : { status: params.status }),
        ...(params.statusReason === undefined
          ? {}
          : { statusReason: params.statusReason }),
        ...(params.statusSource === undefined
          ? {}
          : { statusSource: params.statusSource }),
        ...(params.dryRun === undefined ? {} : { dryRun: params.dryRun }),
        ...(params.ownerId === undefined ? {} : { ownerId: params.ownerId }),
        ...(params.updateTeamTags === undefined
          ? {}
          : { updateTeamTags: params.updateTeamTags }),
        ...(params.leadListId === undefined
          ? {}
          : { leadListId: params.leadListId }),
        ...(params.note === undefined ? {} : { note: params.note }),
        ...(params.workflowId === undefined
          ? {}
          : { workflowId: params.workflowId }),
        ...(params.requestId === undefined
          ? {}
          : { requestId: params.requestId }),
      },
      undefined,
      undefined,
      BULK_ACTION_REQUEST_TIMEOUT_MS
    );
  }

  async getBackgroundJob(jobId: string): Promise<BackgroundJob> {
    const response = await this.enrollhereApi.get<{ job: unknown }>(
      `jobs/${encodeURIComponent(jobId)}/status`
    );
    return BackgroundJob.fromJSON(response.job);
  }

  /** Cooperative: the worker stops between batches, not mid-batch. */
  async cancelBackgroundJob(jobId: string): Promise<BackgroundJob> {
    const response = await this.enrollhereApi.post<{ job: unknown }>(
      `jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return BackgroundJob.fromJSON(response.job);
  }

  async bulkMergePolicies(
    params:
      | {
          pairs: Array<{
            primaryPolicyId: string;
            duplicatePolicyId: string;
            reason: string;
            fieldSources?: Partial<
              Record<
                | 'name'
                | 'birthDate'
                | 'phone'
                | 'email'
                | 'address'
                | 'medicare'
                | 'enrollment'
                | 'effectiveDate'
                | 'submittedAt'
                | 'status'
                | 'baseCategory',
                'primary' | 'secondary'
              >
            >;
          }>;
        }
      | {
          agencyId: string;
          categoryId: string;
          limit?: number;
          nextPageToken?: string;
        }
  ): Promise<{
    results: Array<{
      primaryPolicyId: string;
      duplicatePolicyId: string;
      success: boolean;
      mergedPolicy: Policy | null;
      message: string;
    }>;
    succeeded: number;
    failed: number;
    nextPageToken?: string;
    categoryId?: string;
  }> {
    const response = await this.enrollhereApi.post<{
      results: Array<{
        primaryPolicyId: string;
        duplicatePolicyId: string;
        ok?: boolean;
        success?: boolean;
        mergedPolicy?: unknown;
        message: string;
      }>;
      succeeded: number;
      failed: number;
      nextPageToken?: string;
      categoryId?: string;
    }>('policy/merge/bulk', params);

    return {
      succeeded: response.succeeded ?? 0,
      failed: response.failed ?? 0,
      nextPageToken: response.nextPageToken,
      categoryId: response.categoryId,
      results: (response.results ?? []).map(result => ({
        primaryPolicyId: result.primaryPolicyId,
        duplicatePolicyId: result.duplicatePolicyId,
        success: Boolean(result.ok ?? result.success),
        mergedPolicy: result.mergedPolicy
          ? Policy.fromJSON(result.mergedPolicy)
          : null,
        message: result.message ?? '',
      })),
    };
  }

  /**
   * AI recommendations for merge field survivorship / bulk action (prefill only).
   * Summaries are human-readable (no policy document ids).
   */
  async recommendPolicyMerges(params: {
    agencyId: string;
    pairs: Array<{
      primaryPolicyId: string;
      secondaryPolicyId: string;
    }>;
  }): Promise<{
    recommendations: Array<{
      primaryPolicyId: string;
      secondaryPolicyId: string;
      summary: string;
      action: 'bulk_merge' | 'review' | 'skip';
      confidence: 'high' | 'medium' | 'low';
      fieldSources: Partial<
        Record<
          | 'name'
          | 'birthDate'
          | 'phone'
          | 'email'
          | 'address'
          | 'medicare'
          | 'enrollment'
          | 'effectiveDate'
          | 'submittedAt'
          | 'status'
          | 'baseCategory',
          'primary' | 'secondary'
        >
      >;
    }>;
  }> {
    return this.enrollhereApi.post('policy/merge/recommend', params);
  }

  /**
   * Merge a duplicate policy into the primary policy (rewires commissions/calls,
   * fills gaps on primary, deletes duplicate).
   */
  async mergePolicies(params: {
    primaryPolicyId: string;
    duplicatePolicyId: string;
    reason: string;
    fieldSources?: Partial<
      Record<
        | 'name'
        | 'birthDate'
        | 'phone'
        | 'email'
        | 'address'
        | 'medicare'
        | 'enrollment'
        | 'effectiveDate'
        | 'submittedAt'
        | 'status'
        | 'baseCategory',
        'primary' | 'secondary'
      >
    >;
  }): Promise<{
    success: boolean;
    mergedPolicy: Policy | null;
    message: string;
  }> {
    const { primaryPolicyId, duplicatePolicyId, reason, fieldSources } = params;
    const response = await this.enrollhereApi.post<{
      success?: boolean;
      mergedPolicy?: unknown;
      message: string;
    }>(`policy/${primaryPolicyId}/merge`, {
      duplicatePolicyId,
      reason,
      ...(fieldSources ? { fieldSources } : {}),
    });
    return {
      success: response.mergedPolicy != null || response.success === true,
      mergedPolicy: response.mergedPolicy
        ? Policy.fromJSON(response.mergedPolicy)
        : null,
      message: response.message ?? '',
    };
  }

  /**
   * Get policy history
   */
  async getPolicyHistory(
    policyId: string,
    params?: {
      changeType?: string[];
      changedByUserId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ history: PolicyHistoryEntry[]; total: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        if (params.changeType) {
          params.changeType.forEach(ct => queryParams.append('changeType', ct));
        }
        if (params.changedByUserId) {
          queryParams.append('changedByUserId', params.changedByUserId);
        }
        if (params.startDate) {
          queryParams.append('startDate', params.startDate);
        }
        if (params.endDate) {
          queryParams.append('endDate', params.endDate);
        }
        if (params.limit) {
          queryParams.append('limit', params.limit.toString());
        }
        if (params.offset) {
          queryParams.append('offset', params.offset.toString());
        }
      }
      const queryString = queryParams.toString();
      const url = `policy/${policyId}/history${queryString ? `?${queryString}` : ''}`;
      const response = await this.enrollhereApi.get<{
        history: unknown[];
        total: number;
      }>(url);

      const history: unknown[] = [];
      if (Array.isArray(response.history)) {
        history.push(...response.history);
      }
      return {
        history: history.map((h: any) => PolicyHistoryEntry.fromJSON(h)),
        total: response.total || 0,
      };
    } catch (error) {
      logger.error('Error fetching policy history', error);
      return { history: [], total: 0 };
    }
  }

  /**
   * Update policy
   */
  async updatePolicy(
    policyId: string,
    params: PolicyUpdateRequest
  ): Promise<{
    success: boolean;
    policy?: Policy;
    changedFields?: string[];
    historyEntryIds?: string[];
    indexSyncPending?: boolean;
    warning?: string;
    message?: string;
  }> {
    try {
      const response = await this.enrollhereApi.patch<{
        success: boolean;
        policy?: unknown;
        changedFields?: string[];
        historyEntryIds?: string[];
        indexSyncPending?: boolean;
        warning?: string;
        message?: string;
      }>(`policy/${policyId}`, params);
      return {
        ...response,
        policy: response.policy ? Policy.fromJSON(response.policy) : undefined,
      };
    } catch (error) {
      logger.error('Error updating policy', error);
      throw error;
    }
  }

  /**
   * Replace the permanent tags on a policy.
   *
   * Deliberately its own endpoint rather than a field on `updatePolicy`: tags
   * decide which tagged employees can see the policy, so the generic
   * `PATCH /v1/policy/:id` update map rejects `permanentTags` outright.
   */
  async updatePolicyTags(
    policyId: string,
    tagIds: string[],
    updateReason?: string
  ): Promise<{ policyId: string; tagIds: string[] }> {
    try {
      return await this.enrollhereApi.post<{
        policyId: string;
        tagIds: string[];
      }>(`policy/${policyId}/tags`, {
        tagIds,
        ...(updateReason ? { updateReason } : {}),
      });
    } catch (error) {
      logger.error('Error updating policy tags', error);
      throw error;
    }
  }

  /**
   * Previews which team tags an owner or participant change would add, so the
   * edit form and the bulk owner change can show it before saving. Read-only.
   */
  async previewPolicyTeamTags(
    request: PolicyTeamTagsPreviewRequest
  ): Promise<PolicyTeamTagsPreview> {
    return this.enrollhereApi.post<PolicyTeamTagsPreview>(
      'policy/team-tags-preview',
      request
    );
  }

  /**
   * Get policy statistics using flexible aggregation API
   * Supports multi-dimensional aggregations with custom metrics
   */
  async getAggregatedStats(
    request: AggregationRequest
  ): Promise<AggregationResponse> {
    try {
      return await this.enrollhereApi.post<AggregationResponse>(
        'policy/stats',
        request
      );
    } catch (error) {
      logger.error('Error fetching aggregated stats', error);
      const metrics: string[] = request.metrics.map(m => m.name ?? m.type);
      return {
        dimensions: request.dimensions.map(d => d.field),
        metrics,
        total: 0,
        buckets: request.dimensions.length > 0 ? [] : undefined,
        summary: request.dimensions.length === 0 ? {} : undefined,
      };
    }
  }

  /**
   * @deprecated Use getAggregatedStats instead
   * Legacy method for backward compatibility
   */
  async getPolicyStats(
    filter: any,
    aggregationType: string,
    customAggregation?: { groupBy: string[]; metrics: string[] }
  ): Promise<{ aggregationType: string; data: any }> {
    try {
      const response = await this.enrollhereApi.post<{ data: any }>(
        'policy/stats',
        {
          filter,
          aggregationType,
          customAggregation,
        }
      );
      return { aggregationType, data: response.data || {} };
    } catch (error) {
      logger.error('Error fetching policy stats', error);
      return { aggregationType, data: {} };
    }
  }

  /**
   * Add note to policy
   */
  async addNote(
    policyId: string,
    note: string
  ): Promise<{ success: boolean; historyEntryId?: string }> {
    try {
      const response = await this.enrollhereApi.post<{
        success: boolean;
        historyEntryId?: string;
      }>(`policy/${policyId}/note`, { note });
      return {
        success: response.success || false,
        historyEntryId: response.historyEntryId,
      };
    } catch (error) {
      logger.error('Error adding note', error);
      return { success: false };
    }
  }

  /**
   * Update policy status
   */
  async updatePolicyStatus(
    policyId: string,
    params: {
      status: PolicyStatus;
      statusType?: 'automatic' | 'manual';
      reason: string;
      statusCategory?: PolicyStatusCategory;
      declineReason?: DeclineReason;
      terminationReason?: TerminationReason;
      terminationDate?: string;
    }
  ): Promise<{
    success: boolean;
    policy?: Policy;
    historyEntryId?: string;
  }> {
    try {
      const {
        status,
        statusType,
        reason,
        statusCategory,
        declineReason,
        terminationReason,
        terminationDate,
      } = params;

      const response = await this.enrollhereApi.patch<{
        success: boolean;
        policy?: unknown;
        historyEntryId?: string;
      }>(`policy/${policyId}/status`, {
        status: policyStatusToJSON(status),
        statusType: statusType === 'automatic' ? 'automatic' : 'manual', // should be removed in next update
        reason,
        statusCategory: statusCategory
          ? policyStatusCategoryToJSON(statusCategory)
          : undefined,
        declineReason: declineReason
          ? declineReasonToJSON(declineReason)
          : undefined,
        terminationReason: terminationReason
          ? terminationReasonToJSON(terminationReason)
          : undefined,
        terminationDate: terminationDate
          ? this.toDateContainer(terminationDate)
          : undefined,
      });

      return {
        success: response.success,
        policy: response.policy ? Policy.fromJSON(response.policy) : undefined,
        historyEntryId: response.historyEntryId,
      };
    } catch (error) {
      logger.error('Error updating policy status', error);
      return { success: false };
    }
  }

  /**
   * Bulk-update status for every policy matching the filter (server re-queries).
   */
  async bulkUpdatePolicyStatusByFilter(params: {
    filter: Record<string, unknown>;
    status: PolicyStatus;
    reason?: string;
    source?: 'policy-book' | 'data-health';
    dryRun?: boolean;
    maxPolicies?: number;
    concurrency?: number;
  }): Promise<PolicyStatusBulkResult> {
    try {
      return await this.enrollhereApi.post<PolicyStatusBulkResult>(
        'policy/status/bulk',
        {
          filter: params.filter,
          status: policyStatusToJSON(params.status),
          reason: params.reason,
          source: params.source ?? 'policy-book',
          dryRun: params.dryRun,
          maxPolicies: params.maxPolicies,
          concurrency: params.concurrency,
        },
        undefined,
        undefined,
        BULK_ACTION_REQUEST_TIMEOUT_MS
      );
    } catch (error) {
      logger.error('Error bulk updating policy status', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Bulk status update failed',
      };
    }
  }

  async bulkUpdatePolicyOwnerByFilter(params: {
    filter: Record<string, unknown>;
    ownerId: string;
    /** Omitted means the server adds the new owner's team tags. */
    updateTeamTags?: boolean;
    dryRun?: boolean;
    maxPolicies?: number;
    concurrency?: number;
  }): Promise<PolicyOwnerBulkResult> {
    try {
      return await this.enrollhereApi.post<PolicyOwnerBulkResult>(
        'policy/owner/bulk',
        {
          filter: params.filter,
          ownerId: params.ownerId,
          ...(params.updateTeamTags === undefined
            ? {}
            : { updateTeamTags: params.updateTeamTags }),
          dryRun: params.dryRun,
          maxPolicies: params.maxPolicies,
          concurrency: params.concurrency,
        },
        undefined,
        undefined,
        BULK_ACTION_REQUEST_TIMEOUT_MS
      );
    } catch (error) {
      logger.error('Error bulk updating policy owner', error);
      return {
        success: false,
        error: policyOwnerSaveErrorMessage(error, 'Bulk owner update failed'),
      };
    }
  }

  /**
   * Adds or removes the dialer lead behind every policy matching the filter
   * from a lead list. The server re-queries by filter (`policy-id` /
   * `exclude-policy-id` ride inside it), resolves against the given agency
   * only, and writes each distinct lead once.
   */
  async bulkLeadListByFilter(params: {
    filter: Record<string, unknown>;
    agencyId: string;
    leadListId: string;
    action: 'add' | 'remove';
    note?: string;
    maxPolicies?: number;
    concurrency?: number;
  }): Promise<PolicyLeadListBulkResult> {
    try {
      return await this.enrollhereApi.post<
        Exclude<PolicyLeadListBulkResult, PolicyBulkClientError>
      >(
        'policy/bulk/lead-list',
        {
          filter: params.filter,
          agencyId: params.agencyId,
          leadListId: params.leadListId,
          action: params.action,
          ...(params.note === undefined ? {} : { note: params.note }),
          maxPolicies: params.maxPolicies,
          concurrency: params.concurrency,
        },
        undefined,
        undefined,
        BULK_ACTION_REQUEST_TIMEOUT_MS
      );
    } catch (error) {
      logger.error('Error bulk updating policy lead lists', error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Bulk lead list update failed',
      };
    }
  }

  /**
   * Starts one workflow run per policy matching the filter. The server
   * re-queries by filter (`policy-id` / `exclude-policy-id` ride inside it,
   * like the other policy bulk actions), resolves the selection against the
   * workflow's own agency only, and enforces the same WORKFLOWS_RUN grant
   * as the single `/v1/workflow/:id/run` route.
   */
  async bulkStartWorkflowByFilter(params: {
    filter: Record<string, unknown>;
    agencyId: string;
    workflowId: string;
    maxPolicies?: number;
    concurrency?: number;
    /** Reuse on retry so a replay returns the runs already started. */
    requestId?: string;
  }): Promise<PolicyWorkflowBulkResult> {
    try {
      return await this.enrollhereApi.post<
        Exclude<PolicyWorkflowBulkResult, PolicyBulkClientError>
      >(
        'policy/workflow/bulk',
        {
          filter: params.filter,
          agencyId: params.agencyId,
          workflowId: params.workflowId,
          ...(params.requestId === undefined
            ? {}
            : { requestId: params.requestId }),
          maxPolicies: params.maxPolicies,
          concurrency: params.concurrency,
        },
        undefined,
        undefined,
        BULK_ACTION_REQUEST_TIMEOUT_MS
      );
    } catch (error) {
      logger.error('Error bulk starting policy workflows', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Bulk workflow start failed',
      };
    }
  }

  /**
   * Get calls for a policy
   */
  async getCallsForPolicy(
    policyId: string,
    params?: {
      orderBy?: { field: string; direction: 'asc' | 'desc' };
      limit?: number;
    }
  ): Promise<{ calls: DialerLeadCall[]; total: number }> {
    try {
      const { limit = 100 } = params || {};

      const response = await this.enrollhereApi.post<{
        calls: unknown[];
        total: number;
      }>('dialer/search/calls', {
        aggregations: {},
        properties: {},
        filter: {
          'policy.list.id': policyId,
        },
        orderBy: {
          field: 'createdAt',
          direction: 'desc',
        },
        size: limit,
      });

      return {
        calls: response.calls?.map(c => DialerLeadCall.fromJSON(c)) || [],
        total: response.total || response.calls?.length || 0,
      };
    } catch (error) {
      logger.error('Error fetching calls for policy', error);
      return { calls: [], total: 0 };
    }
  }

  /**
   * Get commissions for a policy
   */
  async getCommissionsForPolicy(
    _policyId: string,
    _params?: {
      orderBy?: { field: string; direction: 'asc' | 'desc' };
      limit?: number;
    }
  ): Promise<{ commissions: any[]; total: number }> {
    // TODO: Implement when commission search endpoint is available
    return { commissions: [], total: 0 };
  }

  /**
   * Find manual matches for a call
   * Uses the manual matching endpoint to find potential policy matches
   */
  async findManualMatches(params: {
    callData: {
      lead?: {
        phone?: string;
        firstName?: string;
        lastName?: string;
      };
      agent?: { id?: string };
      createdAt?: number;
    };
    agencyId?: string;
    matchingOptions?: {
      dateRangeDays?: number;
      minimumConfidence?: number;
    };
  }): Promise<{
    matches: Array<{
      policy: Policy;
      confidence: number;
      matchReasons: string[];
    }>;
    total: number;
  }> {
    try {
      const response = await this.enrollhereApi.post<{
        matches: Array<{
          policy: unknown;
          confidence: number;
          matchReasons: string[];
        }>;
        total: number;
      }>('policy/manual-match', params);

      return {
        matches: (response.matches || []).map(match => ({
          policy: Policy.fromJSON(match.policy),
          confidence: match.confidence,
          matchReasons: match.matchReasons || [],
        })),
        total: response.total || 0,
      };
    } catch (error) {
      logger.error('Error finding manual matches', error);
      return { matches: [], total: 0 };
    }
  }

  async crosswalkPolicies(params: {
    agencyId: string;
    predecessorPolicyId: string;
    successorPolicyId: string;
    dryRun?: boolean;
    confirmCarrierReplacement?: boolean;
  }): Promise<{
    candidate?: PolicyCrosswalkCandidate;
    relation?: PolicyCrosswalk;
    alreadyLinked?: boolean;
  }> {
    const response = await this.enrollhereApi.post<{
      success: boolean;
      candidate?: unknown;
      relation?: unknown;
      alreadyLinked?: boolean;
    }>('policy/crosswalk', params);
    if (response.success !== true) {
      throw new Error('Could not crosswalk these policies');
    }
    return {
      candidate: response.candidate
        ? PolicyCrosswalkCandidate.fromJSON(response.candidate)
        : undefined,
      relation: response.relation
        ? PolicyCrosswalk.fromJSON(response.relation)
        : undefined,
      alreadyLinked: response.alreadyLinked,
    };
  }

  async listDuplicateFindings(params: {
    agencyId: string;
    size?: number;
    nextPageToken?: string;
    resolutionState?: number;
    categoryId?: string;
    bulkMergeEligible?: boolean;
    includeAggregations?: boolean;
  }): Promise<{
    findings: PolicyDuplicateReviewFinding[];
    totalFindings: number;
    totalPolicies: number;
    nextPageToken?: string;
    runTimestamp?: string | Date;
    aggregations?: {
      byCategory: Array<{
        categoryId: string;
        categoryLabel?: string;
        count: number;
      }>;
      bulkMergeEligible: number;
      reviewOnly: number;
    };
  }> {
    try {
      const response = await this.enrollhereApi.post<{
        findings?: Array<
          Omit<PolicyDuplicateReviewFinding, 'policies'> & {
            policies?: unknown[];
          }
        >;
        totalFindings?: number;
        totalPolicies?: number;
        nextPageToken?: string;
        runTimestamp?: string | Date;
        aggregations?: {
          byCategory?: Array<{
            categoryId: string;
            categoryLabel?: string;
            count: number;
          }>;
          bulkMergeEligible?: number;
          reviewOnly?: number;
        };
      }>('policy/duplicates/findings', {
        agencyId: params.agencyId,
        size: params.size,
        nextPageToken: params.nextPageToken,
        resolutionState: params.resolutionState,
        categoryId: params.categoryId,
        bulkMergeEligible: params.bulkMergeEligible,
        includeAggregations: params.includeAggregations,
      });

      const findings = (response.findings ?? []).flatMap(finding => {
        if (
          !finding ||
          typeof finding !== 'object' ||
          !Array.isArray(finding.policies)
        ) {
          return [];
        }

        const policies = finding.policies.flatMap(policy => {
          try {
            return [Policy.fromJSON(policy)];
          } catch {
            return [];
          }
        });

        return [
          {
            ...finding,
            policies,
            crosswalkCandidates: (finding.crosswalkCandidates ?? []).map(
              candidate => PolicyCrosswalkCandidate.fromJSON(candidate)
            ),
          },
        ];
      });

      const aggregations = response.aggregations
        ? {
            byCategory: (response.aggregations.byCategory ?? []).map(entry => ({
              categoryId: entry.categoryId,
              categoryLabel: entry.categoryLabel,
              count: entry.count ?? 0,
            })),
            bulkMergeEligible: response.aggregations.bulkMergeEligible ?? 0,
            reviewOnly: response.aggregations.reviewOnly ?? 0,
          }
        : undefined;

      return {
        findings,
        totalFindings: response.totalFindings ?? 0,
        totalPolicies: response.totalPolicies ?? 0,
        nextPageToken: response.nextPageToken,
        runTimestamp: response.runTimestamp,
        aggregations,
      };
    } catch (error) {
      logger.error('Error listing duplicate findings', error);
      return { findings: [], totalFindings: 0, totalPolicies: 0 };
    }
  }

  async dismissDuplicateGroup(params: {
    agencyId: string;
    fingerprint: string;
  }): Promise<{ success: boolean }> {
    try {
      const response = await this.enrollhereApi.post<{ success?: boolean }>(
        'policy/duplicates/dismiss',
        params
      );
      return { success: response?.success === true };
    } catch (error) {
      logger.error('Error dismissing duplicate group', error);
      throw error;
    }
  }

  /**
   * Sync a source (call, enrollment, etc.) to a policy
   */
  async syncSourceToPolicy(params: {
    policyId: string;
    sourceType: string;
    sourceId: string;
    leadId?: string;
    agencyId?: string;
  }): Promise<{ success: boolean }> {
    try {
      const { policyId, sourceType, sourceId, leadId, agencyId } = params;

      const response = await this.enrollhereApi.post<{ success: boolean }>(
        `policy/${policyId}/sync`,
        {
          sourceType,
          sourceId,
          leadId,
          agencyId,
        }
      );
      return response || { success: true };
    } catch (error) {
      logger.error('Error syncing source to policy', error);
      throw error;
    }
  }

  async refreshPolicyMetadata(params: {
    policyIds: string[];
    filter?: unknown;
    refreshMetrics?: boolean;
    refreshStatus?: boolean;
    relinkCalls?: boolean;
    dryRun?: boolean;
  }): Promise<{
    success: boolean;
    totalFound?: number;
    totalProcessed?: number;
    results?: Array<{
      policyId?: string;
      agencyId?: string;
      success?: boolean;
      relinkedCalls?: number;
      statusUpdated?: boolean;
      metricsUpdated?: boolean;
      error?: string;
    }>;
  }> {
    try {
      return await this.enrollhereApi.post<{
        success: boolean;
        totalFound?: number;
        totalProcessed?: number;
        results?: Array<{
          policyId?: string;
          agencyId?: string;
          success?: boolean;
          relinkedCalls?: number;
          statusUpdated?: boolean;
          metricsUpdated?: boolean;
          error?: string;
        }>;
      }>('policy/refresh-metadata', params);
    } catch (error) {
      logger.error('Error refreshing policy metadata', error);
      return { success: false };
    }
  }

  async refreshPolicyMetadataBulk(params: {
    filter?: unknown;
    policyIds?: string[];
    size?: number;
    nextPageToken?: string;
    refreshMetrics?: boolean;
    refreshStatus?: boolean;
    relinkCalls?: boolean;
    dryRun?: boolean;
    concurrency?: number;
    bypassVisibility?: boolean;
  }): Promise<{ jobId?: string; status?: string }> {
    try {
      return await this.enrollhereApi.post<{ jobId?: string; status?: string }>(
        'policy/refresh-metadata/bulk',
        params
      );
    } catch (error) {
      logger.error('Error enqueueing policy metadata refresh', error);
      return {};
    }
  }

  async getOrphanedCalls(params?: {
    agencyId?: string;
    orderBy?: { field: string; direction: 'asc' | 'desc' };
    limit?: number;
    nextPageToken?: string;
  }): Promise<{
    calls: DialerLeadCall[];
    total: number;
    nextPageToken?: string;
  }> {
    try {
      const { agencyId, orderBy, limit = 100, nextPageToken } = params || {};

      const filter: any = {
        result: DISPO_LIST_SALE.map(result =>
          consumer_Dialer_ResultToJSON(result)
        ),
        'policy.empty': true,
      };

      if (agencyId) {
        filter['agency.id'] = agencyId;
      }

      const response = await this.enrollhereApi.post<{
        calls: unknown[];
        total: number;
        nextPageToken?: string;
      }>('dialer/search/calls', {
        aggregations: {},
        properties: {},
        filter,
        orderBy: orderBy || {
          field: 'createdAt',
          direction: 'desc',
        },
        size: limit,
        nextPageToken,
      });

      return {
        calls: response.calls?.map(c => DialerLeadCall.fromJSON(c)) || [],
        total: response.total || response.calls?.length || 0,
        nextPageToken: response.nextPageToken,
      };
    } catch (error) {
      logger.error('Error fetching orphaned calls', error);
      return { calls: [], total: 0 };
    }
  }

  async getOrphanedEnrollments(params?: {
    agencyId?: string;
    orderBy?: { field: string; direction: 'asc' | 'desc' };
    limit?: number;
    nextPageToken?: string;
  }): Promise<{
    enrollments: Enrollment[];
    total: number;
    nextPageToken?: string;
  }> {
    try {
      const { agencyId, orderBy, limit = 100, nextPageToken } = params || {};

      const filter: any = {
        'policy.empty': true,
      };

      if (agencyId) {
        filter['agency.id'] = agencyId;
      }

      const response = await this.enrollhereApi.post<{
        enrollments: unknown[];
        total: number;
        nextPageToken?: string;
      }>('enrollment/search', {
        aggregations: {},
        properties: {},
        filter,
        orderBy: orderBy || {
          field: 'details.signatureDate.date',
          direction: 'desc',
        },
        size: limit,
        nextPageToken,
      });

      return {
        enrollments:
          response.enrollments?.map(e => Enrollment.fromJSON(e)) || [],
        total: response.total || response.enrollments?.length || 0,
        nextPageToken: response.nextPageToken,
      };
    } catch (error) {
      logger.error('Error fetching orphaned enrollments', error);
      return { enrollments: [], total: 0 };
    }
  }

  async identifyPolicy(params: {
    sourceType: Type;
    sourceData: unknown;
    agencyId?: string;
    createIfNotFound?: boolean;
    updateEntity?: boolean;
    entityId?: string;
  }): Promise<{
    success: boolean;
    policyId?: string;
    policy?: Policy;
    isNew?: boolean;
    matchConfidence?: number;
    matchReasons?: string[];
  }> {
    try {
      const response = await this.enrollhereApi.post<{
        policyId?: string;
        policy?: unknown;
        isNew?: boolean;
        matchConfidence?: number;
        matchReasons?: string[];
      }>('policy/identify', {
        ...params,
        sourceType: typeToJSON(params.sourceType),
      });

      return {
        success: !!response.policyId,
        policyId: response.policyId,
        policy: response.policy ? Policy.fromJSON(response.policy) : undefined,
        isNew: response.isNew,
        matchConfidence: response.matchConfidence,
        matchReasons: response.matchReasons || [],
      };
    } catch (error) {
      logger.error('Error identifying policy', error);
      throw error;
    }
  }

  /**
   * Backfill policies for an agency via server API
   * Queues enrollments/commissions for background processing
   * Note: Jobs are queued asynchronously - results may take time to appear
   * Supports pagination for handling millions of records
   */
  async backfillPolicies(params: {
    agencyId: string;
    recordTypes?: ('enrollments' | 'commissions')[];
    limit?: number;
  }): Promise<{
    success: boolean;
    stats: {
      enrollmentsQueued: number;
      commissionsQueued: number;
      errors: number;
      enrollmentPages: number;
      commissionPages: number;
    };
  }> {
    try {
      const callable = httpsCallable<
        {
          agencyId: string;
          recordTypes?: ('enrollments' | 'commissions')[];
          limit?: number;
        },
        {
          success: boolean;
          stats: {
            enrollmentsQueued: number;
            commissionsQueued: number;
            errors: number;
            enrollmentPages: number;
            commissionPages: number;
          };
        }
      >(this.functions, 'backfillPolicies');

      const result = await callable(params);
      return result.data;
    } catch (error) {
      logger.error('Error backfilling policies', error);
      throw error;
    }
  }

  private toDateContainer(dateString: string) {
    const [yearStr, monthStr, dayStr] = dateString.split('-');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    const utcNoon = new Date(`${dateString}T12:00:00.000Z`);
    return {
      timestamp: utcNoon.getTime(),
      date: utcNoon.toISOString(),
      day,
      month,
      year,
    };
  }
}

const TERMINAL_JOB_STATUSES: ReadonlySet<BackgroundJobStatus> = new Set([
  BackgroundJobStatus.BJS_COMPLETED,
  BackgroundJobStatus.BJS_FAILED,
  BackgroundJobStatus.BJS_CANCELLED,
]);

/**
 * Polls a background job to completion, matching the 2s interval and
 * transient-failure tolerance the dialer's `pollBulkLeadActionJob` uses for
 * `BJT_BULK_LEAD_ACTION`. Generic over any job type (not just
 * `BJT_BULK_POLICY_ACTION`) since polling only depends on `job.status`; the
 * caller's `onProgress` reads whichever result field its job type uses.
 *
 * A job this covers can run for minutes, so a single flaky network blip
 * must not abort an otherwise-successful run. Transient poll failures are
 * retried up to `maxConsecutiveErrors` in a row before giving up; a run of
 * successful polls resets that count.
 */
export async function pollBackgroundJob(params: {
  jobId: string;
  fetchStatus: (jobId: string) => Promise<BackgroundJob>;
  onProgress?: (job: BackgroundJob) => void;
  intervalMs?: number;
  /** Safety bound so a stuck job cannot poll forever if the tab stays open. */
  maxAttempts?: number;
  /** Consecutive failed polls tolerated before giving up and rethrowing. */
  maxConsecutiveErrors?: number;
  /** Cooperative cancellation; checked before each poll. */
  isCancelled?: () => boolean;
}): Promise<BackgroundJob> {
  const intervalMs = params.intervalMs ?? 2000;
  const maxAttempts = params.maxAttempts ?? 1000; // ~33 minutes at 2s
  const maxConsecutiveErrors = params.maxConsecutiveErrors ?? 5;
  let attempt = 0;
  let consecutiveErrors = 0;

  const poll = async (): Promise<BackgroundJob> => {
    if (attempt >= maxAttempts) {
      throw new Error(
        `Background job ${params.jobId} did not finish after ${maxAttempts} polls`
      );
    }
    attempt += 1;
    try {
      const job = await params.fetchStatus(params.jobId);
      consecutiveErrors = 0;
      return job;
    } catch (error) {
      consecutiveErrors += 1;
      if (consecutiveErrors > maxConsecutiveErrors) {
        throw error;
      }
      logger.warn('Background job status poll failed; retrying', {
        jobId: params.jobId,
        consecutiveErrors,
        error,
      });
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      return poll();
    }
  };

  let job = await poll();
  params.onProgress?.(job);

  while (!TERMINAL_JOB_STATUSES.has(job.status) && attempt < maxAttempts) {
    if (params.isCancelled?.()) {
      return job;
    }
    await new Promise(resolve => setTimeout(resolve, intervalMs));
    job = await poll();
    params.onProgress?.(job);
  }

  if (!TERMINAL_JOB_STATUSES.has(job.status)) {
    throw new Error(
      `Background job ${params.jobId} did not finish after ${maxAttempts} polls`
    );
  }

  return job;
}
